import { getHaklaiPropertyDepreciationUiState } from "./depreciation/haklaiPropertyDepreciationUiState";
import {
  getHaklaiLogicalPropertyPartKey,
  mergeHaklaiPartKeyedDepreciationProfile
} from "./haklaiFlowHelpers";
import {
  getHaklaiPartDepreciationMode,
  hasHaklaiSelectablePurchaseDepreciationProfile,
  HAKLAI_PROPERTY_DEPRECIATION_MODE
} from "./haklaiDepreciationHelpers";

const areFlatObjectsEqual = (first = {}, second = {}) => {
  const firstKeys = Object.keys(first || {});
  const secondKeys = Object.keys(second || {});
  if (firstKeys.length !== secondKeys.length) return false;
  return firstKeys.every((key) => first[key] === second[key]);
};

export const normalizeHaklaiPurchaseDepreciationStateForLogicalParts = (
  mainCalc = {},
  logicalPropertyPartsData = {}
) => {
  const propertyPartsData = mainCalc.propertyPartsData || {};
  const purchaseDepreciationByPart = Object.entries(mainCalc.purchaseDepreciationByPart || {})
    .reduce((acc, [partKey, depreciation]) => {
      const logicalPartKey = getHaklaiLogicalPropertyPartKey({
        partKey,
        propertyPartsData,
        logicalPropertyPartsData
      });
      const part = logicalPropertyPartsData[logicalPartKey];
      if (getHaklaiPartDepreciationMode(part) === HAKLAI_PROPERTY_DEPRECIATION_MODE.MANUAL_FORM_11) {
        acc[logicalPartKey] = mergeHaklaiPartKeyedDepreciationProfile(acc[logicalPartKey], depreciation);
      }
      return acc;
    }, {});

  const purchaseDepreciationSelectionByPart = Object.entries(mainCalc.purchaseDepreciationSelectionByPart || {})
    .reduce((acc, [partKey, isSelected]) => {
      const logicalPartKey = getHaklaiLogicalPropertyPartKey({
        partKey,
        propertyPartsData,
        logicalPropertyPartsData
      });
      const part = logicalPropertyPartsData[logicalPartKey];
      const hasPurchaseProfile = hasHaklaiSelectablePurchaseDepreciationProfile({
        partKey: logicalPartKey,
        part,
        residentialRentalByPart: mainCalc.residentialRentalByPart || {},
        purchaseDepreciationByPart
      });
      if (isSelected === true && part && hasPurchaseProfile) {
        acc[logicalPartKey] = true;
      }
      return acc;
    }, {});

  return {
    changed:
      !areFlatObjectsEqual(mainCalc.purchaseDepreciationByPart || {}, purchaseDepreciationByPart)
      || !areFlatObjectsEqual(mainCalc.purchaseDepreciationSelectionByPart || {}, purchaseDepreciationSelectionByPart),
    purchaseDepreciationByPart,
    purchaseDepreciationSelectionByPart
  };
};

const hasDisplayablePurchaseDepreciationAmount = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && Math.round(numericValue) > 0;
};

// The user chooses which purchase depreciation to deduct. A saved choice
// stays only while a real amount still exists. An amount by itself is not
// a choice, and a choice without an amount is dropped.
export const syncHaklaiAutomaticPurchaseDepreciationSelection = ({
  currentSelection = {},
  logicalPropertyPartsData = {},
  residentialRentalByPart = {},
  purchaseDepreciationByPart = {},
  soldPropertyDate = "",
  purchaseDepreciationStartDate = "",
  computedAcquisitionByPart = {}
} = {}) => {
  const purchaseDepreciationSelectionByPart = Object.entries(logicalPropertyPartsData || {})
    .reduce((acc, [partKey, part]) => {
      if (!part || currentSelection?.[partKey] !== true) return acc;
      const depreciationState = getHaklaiPropertyDepreciationUiState({
        partKey,
        part,
        residentialRentalByPart,
        purchaseDepreciationByPart,
        soldPropertyDate,
        purchaseDepreciationStartDate,
        acquisitionAmount: computedAcquisitionByPart[partKey]
      });
      if (
        depreciationState.isComplete === true
        && hasDisplayablePurchaseDepreciationAmount(depreciationState.depreciationResult)
      ) {
        acc[partKey] = true;
      }
      return acc;
    }, {});

  return {
    changed: !areFlatObjectsEqual(currentSelection || {}, purchaseDepreciationSelectionByPart),
    purchaseDepreciationSelectionByPart
  };
};
